// Панель уведомлений: что произошло, пока человек смотрел в другое место.
//
// Панель, а не страница: уведомление читают, не бросая работу. Открывается из полосы разделов
// в любом разделе, адрес запоминается (?p=notifications), Esc закрывает — правило 4.
//
// Переход по строке ведёт в раздел с уже наложенным отбором, а не «куда-нибудь». Уведомление,
// после которого надо самому искать, чего оно касалось, — это не уведомление, а шум.

import { RiNotification3Line } from '@remixicon/react';
import { Link } from 'react-router';

import type { NotificationItem } from '@/shared/api/endpoints/notifications';
import * as Badge from '@/shared/ui/badge';
import * as Button from '@/shared/ui/button';
import type { ApiError } from '@/shared/errors/api-error';
import { ListSkeleton } from '@/shared/ui/list-row';
import { EmptyState, ScreenState } from '@/shared/ui/screen-state';
import { formatDateDMY, formatTimeHM, parseMoment } from '@/shared/time/format';

import { useMarkAllRead, useMarkRead, useNotificationFeed } from './queries';

/**
 * Куда ведёт уведомление. Сущность приходит с сервера, адрес знает только кабинет.
 *
 * Адреса написаны строками, а не собраны из `route.ts` разделов: уведомления живут в общем
 * каталоге, и импорт разделов отсюда потянул бы весь их код в полосу разделов, которая
 * рисуется всегда. Строки при этом не выдуманы — они совпадают с представлениями разделов,
 * и расхождение ловит сквозная проверка перехода.
 *
 * **Заявки на выдачу в кабинете своего экрана пока не имеют** (сделаны для приложения,
 * сценарий М7), поэтому уведомление о просьбе ведёт на пульт подотчёта — туда, где видно
 * человека и его остаток. Ссылка на несуществующее представление была бы хуже отсутствия
 * ссылки: человек нажимает и попадает в пустоту.
 */
function адресПерехода(item: NotificationItem): string | null {
  switch (item.entity) {
    case 'expense_claim':
      return item.entity_id === null || item.entity_id === undefined
        ? '/money/claims'
        : `/money/claims?p=claim:${item.entity_id}`;
    case 'cash_request':
    case 'cash_issue':
    case 'cash_transfer':
    case 'accountable_person':
      return '/money/accountable';
    case 'payroll':
      return '/payroll/sheets';
    // Долг уволенного. Карточку человека отсюда не открыть — в уведомлении лежит долг, а
    // не человек, — поэтому ведём в реестр с уже наложенным отбором «с непогашенным
    // долгом». Список, в котором нужный человек виден сразу, полезнее ссылки в никуда.
    case 'debt':
      return '/people/employees?f.debt=true';
    default:
      return null;
  }
}

function когда(значение: string): string {
  const момент = parseMoment(значение);
  if (момент === null) {
    return '';
  }
  const сегодня = new Date();
  const тотЖеДень =
    момент.getFullYear() === сегодня.getFullYear() &&
    момент.getMonth() === сегодня.getMonth() &&
    момент.getDate() === сегодня.getDate();
  return тотЖеДень ? formatTimeHM(момент) : `${formatDateDMY(момент)}, ${formatTimeHM(момент)}`;
}

export function NotificationsPanel({ onClose }: { onClose: () => void }) {
  const лента = useNotificationFeed(false);
  const пометить = useMarkRead();
  const пометитьВсе = useMarkAllRead();

  return (
    <ScreenState
      data={лента.data}
      error={лента.error as ApiError | null}
      subject='ленту уведомлений'
      isEmpty={(данные) => данные.items.length === 0}
      onRetry={() => void лента.refetch()}
      loading={<ListSkeleton rows={5} />}
      empty={
        <EmptyState
          icon={RiNotification3Line}
          title='Уведомлений пока нет'
          description='Здесь появятся просьбы о деньгах, решения по отчётам и движения подотчёта — по мере того, как они происходят.'
        />
      }
    >
      {(данные) => (
        <div className='flex h-full flex-col'>
          <div className='flex items-center justify-between gap-2 border-b border-stroke-soft-200 px-4 py-3'>
            <span className='text-paragraph-sm text-text-sub-600'>
              {данные.unread === 0 ? 'Всё прочитано' : `Непрочитанных: ${данные.unread}`}
            </span>
            {данные.unread > 0 ? (
              <Button.Root
                variant='neutral'
                mode='stroke'
                size='xsmall'
                onClick={() => void пометитьВсе()}
              >
                Прочитано всё
              </Button.Root>
            ) : null}
          </div>

          <ul className='flex-1 divide-y divide-stroke-soft-200 overflow-y-auto'>
            {данные.items.map((item) => {
              const адрес = адресПерехода(item);
              const непрочитано = item.read_at === null || item.read_at === undefined;
              const содержимое = (
                <div className='flex flex-col gap-1 px-4 py-3'>
                  <div className='flex items-start justify-between gap-2'>
                    <span className='text-label-sm text-text-strong-950'>{item.title}</span>
                    <span className='shrink-0 text-paragraph-xs text-text-sub-600'>
                      {когда(item.created_at)}
                    </span>
                  </div>
                  {item.body === null || item.body === undefined || item.body === '' ? null : (
                    <span className='text-paragraph-sm text-text-sub-600'>{item.body}</span>
                  )}
                  <div className='flex items-center gap-2'>
                    <Badge.Root
                      variant='lighter'
                      color={непрочитано ? 'blue' : 'gray'}
                      size='medium'
                    >
                      {item.type_title}
                    </Badge.Root>
                  </div>
                </div>
              );

              return (
                <li key={item.id} className={непрочитано ? 'bg-bg-weak-50' : undefined}>
                  {адрес === null ? (
                    содержимое
                  ) : (
                    <Link
                      to={адрес}
                      onClick={() => {
                        if (непрочитано) {
                          void пометить([item.id]);
                        }
                        onClose();
                      }}
                      className='block transition duration-200 ease-out hover:bg-bg-weak-50'
                    >
                      {содержимое}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </ScreenState>
  );
}
